import { AppKitNetwork, berachain, berachainBepolia } from '@reown/appkit/networks'
import { defineChain, parseGwei } from 'viem'

export const robinhood = defineChain({
  id: 4663,
  name: 'Robinhood Chain',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: {
    default: { http: ['https://rpc.mainnet.chain.robinhood.com'] },
  },
  blockExplorers: {
    default: { name: 'Etherscan', url: 'https://robin.etherscan.io/' },
  },
  contracts: {
    multicall3: { address: '0xcA11bde05977b3631167028862bE2a173976CA11' },
  },
})

export const PRIORITY_FEE_PER_GAS = parseGwei('1')

const withPriorityFee = (network: AppKitNetwork): AppKitNetwork => ({
  ...network,
  fees: {
    ...network.fees,
    maxPriorityFeePerGas: PRIORITY_FEE_PER_GAS,
  },
})

export enum SupportedChainId {
  BERA = berachain.id,
  BEPOLIA = berachainBepolia.id,
  ROBINHOOD = robinhood.id,
}

export const CHAIN_IDS_TO_NAMES = {
  [SupportedChainId.BERA]: 'berachain_mainnet',
  [SupportedChainId.BEPOLIA]: 'berachain_bepolia',
  [SupportedChainId.ROBINHOOD]: 'robinhood_mainnet',
}

export const networks = [withPriorityFee(berachain), withPriorityFee(robinhood)] satisfies [
  AppKitNetwork,
  ...AppKitNetwork[]
]

// Block time polling interval for blocks + transaction receipt
export const pollingInterval = {
  [SupportedChainId.BERA]: 2_000,
  [SupportedChainId.BEPOLIA]: 2_000,
  [SupportedChainId.ROBINHOOD]: 1_000,
}

export function defaultChainId(): SupportedChainId {
  const chainId = parseInt(import.meta.env.VITE_DEFAULT_CHAIN_ID || '')

  if (isNaN(chainId)) {
    throw new Error('VITE_DEFAULT_CHAIN_ID is required and must be a valid number.')
  }

  return chainId as SupportedChainId
}

export const ALL_SUPPORTED_CHAIN_IDS = networks.map((n) => n.id) as SupportedChainId[]

export const defaultNetwork: AppKitNetwork | undefined = networks.find((n) => n.id === defaultChainId())
if (!defaultNetwork) {
  throw new Error(`Default network with chainId ${defaultChainId()} is not in the list of supported networks.`)
}

export function isSupportedChain(chainId: number | undefined): chainId is SupportedChainId {
  return !!chainId && networks.map((n) => n.id).includes(chainId as SupportedChainId)
}

export const L1_CHAIN_IDS = [SupportedChainId.BEPOLIA, SupportedChainId.BERA, SupportedChainId.ROBINHOOD] as const

export type SupportedL1ChainId = (typeof L1_CHAIN_IDS)[number]

export const chainIdToNativeCurrencyName: Record<SupportedChainId, string> = {
  [SupportedChainId.BERA]: berachain.nativeCurrency.symbol,
  [SupportedChainId.BEPOLIA]: berachainBepolia.nativeCurrency.symbol,
  [SupportedChainId.ROBINHOOD]: robinhood.nativeCurrency.symbol,
}
